import type { TvChannel, TvShow, Vod } from '@setplex/tria-api'

import { api } from '@setplex/tria-api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { recentlyWatchedModel } from '~/features/recently-watched'

export const reset = createEvent()

/*
 * Featured rows fallbacks
 * Get featured movies, series and live channels using single API call to /v3/carousels/featured
 * and then split and map response into separate arrays for separate rows
 */

const $featured = createStore<{
  movies: Vod[]
  series: TvShow[]
  channels: TvChannel[]
} | null>(null).reset(reset)

const getFeaturedCarouselFx = attach({
  source: $featured,
  effect: async (featured) => {
    if (featured != null) return featured

    const carousels = await api.banners.getFeaturedCarouselFx()
    const movies: Vod[] = []
    const series: TvShow[] = []
    const channels: TvChannel[] = []

    let content
    for (const carousel of carousels) {
      switch (carousel.type) {
        case 'VOD':
          content = carousel.content.map((c) => c.vod!)
          movies.push(...content)
          break
        case 'TV_SHOW':
          content = carousel.content.map((c) => c.tvShow!)
          series.push(...content)
          break
        case 'TV_CHANNEL':
          content = carousel.content.map((c) => c.channel!)
          channels.push(...content)
          break
      }
    }

    return { movies, series, channels }
  },
})

sample({
  clock: getFeaturedCarouselFx.doneData,
  target: $featured,
})

// fallback getter for featured movies
export const featuredMoviesFallbackFx = createEffect(async () => {
  const featured = await getFeaturedCarouselFx()
  return Ok(featured.movies)
})

// fallback getter for featured series
export const featuredSeriesFallbackFx = createEffect(async () => {
  const featured = await getFeaturedCarouselFx()
  return Ok(featured.series)
})

// fallback getter for featured live channels
export const featuredLiveChannelsFallbackFx = createEffect(async () => {
  const featured = await getFeaturedCarouselFx()
  return Ok(featured.channels)
})

/*
 * Recently watched fallback
 * Get recently watched channels using localy stored data
 */

// fallback getter for recently watched channels
export const recentlyWatchedLiveChannelsFallbackFx = attach({
  source: recentlyWatchedModel.$recentlyWatchedFormat,
  effect: async (recentlyWatched) => Ok(Slice(recentlyWatched)),
})

//
// Helpers to create fallback responses
//

function Ok<T>(payload: T) {
  return {
    status: 'OK',
    payload,
  }
}

function Slice<T>(content: T[]) {
  return {
    content,
    first: true,
    last: true,
    number: 0,
    size: content.length,
  }
}
